import type {
  InstructorMonthlyPayoutLineDTO,
  InstructorSessionByInstructorItemDTO,
} from "../api/client";
import { payoutKindLabel, sessionLocationLabel, sourceTypeLabel } from "./instructor-payout-display";

function escapeCsvCell(value: string | number): string {
  const s = String(value);
  if (
    s.includes(",") ||
    s.includes('"') ||
    s.includes("\n") ||
    s.includes("\r")
  ) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function downloadCsv(filename: string, rows: string[]) {
  const csv = rows.join("\n");
  const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function exportInstructorPayoutSummaryCsv(
  run: { year: number; month: number },
  dateFrom: string,
  dateTo: string,
  rows: InstructorSessionByInstructorItemDTO[],
) {
  const header = [
    "ຄູສອນ",
    "ເລກບັນຊີ",
    "ລວມຄັ້ງ",
    "ຄລາດສ໌",
    "ສ່ວນຕົວ",
    "ສ່ວນຕົວ_studio",
    "ສ່ວນຕົວ_ໃກ້",
    "ສ່ວນຕົວ_ໄກ",
    "ຄລາດສ໌_studio",
    "ຄລາດສ໌_ນອກສະຖານທີ່",
    "partner_class_only",
    "ຊ່ວຍສອນເຊົ່າຫ້ອງ_ຄັ້ງ",
    "ຊ່ວຍສອນຄລາດສ໌_TA_ຄັ້ງ",
    "ຊົ່ວໂມງລວມ",
    "ຊົ່ວໂມງສ່ວນຕົວ_studio",
    "ຊົ່ວໂມງສ່ວນຕົວ_ໃກ້",
    "ຊົ່ວໂມງສ່ວນຕົວ_ໄກ",
    "ຄ່າຈ້າງສ່ວນຕົວ_studio_ກີບ",
    "ຄ່າຈ້າງສ່ວນຕົວ_ໃກ້_ກີບ",
    "ຄ່າຈ້າງສ່ວນຕົວ_ໄກ_ກີບ",
    "ຄ່າຈ້າງກຸ່ມ_ກີບ",
    "ຄ່າຈ້າງ partner_ກີບ",
    "ຄ່າຈ້າງຊ່ວຍສອນເຊົ່າຫ້ອງ_ກີບ",
    "ຄ່າຈ້າງຊ່ວຍສອນຄລາດສ໌_TA_ກີບ",
    "quota_session_ຟຣີ",
    "retainer_free_sessions",
    "billable_lead_sessions",
    "ປະເມີນຄ່າຈ້າງລວມ (ກີບ)",
  ].join(",");

  const body = rows.map((r) =>
    [
      r.instructorName,
      r.bankAccountNumber ?? "",
      r.sessionsTaught,
      r.generalClassSessions,
      r.privateLessonSessions,
      r.studioPrivateLessonSessions,
      r.offSiteNearPrivateLessonSessions ?? 0,
      r.offSiteFarPrivateLessonSessions ?? 0,
      r.studioClassSessions,
      r.offSiteClassSessions,
      r.partnerInstructorOnlyClassSessions,
      r.roomRentalAssistSessions ?? 0,
      r.generalClassAssistSessions ?? 0,
      r.billableHoursTotal,
      r.billableHoursPrivateLessonStudio,
      r.billableHoursPrivateLessonOffSiteNear ?? 0,
      r.billableHoursPrivateLessonOffSiteFar ?? 0,
      r.estimatedPayoutPrivateLessonStudio,
      r.estimatedPayoutPrivateLessonOffSiteNear ?? 0,
      r.estimatedPayoutPrivateLessonOffSiteFar ?? 0,
      r.estimatedPayoutGeneralClass,
      r.estimatedPayoutPartner,
      r.estimatedPayoutRoomRentalAssist ?? 0,
      r.estimatedPayoutGeneralClassAssist ?? 0,
      r.monthlyFreeGeneralClassSessions ?? 0,
      r.retainerFreeGeneralClassSessions ?? 0,
      r.billableGeneralClassLeadSessions ?? 0,
      r.estimatedPayout,
    ]
      .map(escapeCsvCell)
      .join(","),
  );

  downloadCsv(
    `instructor-monthly-payout-summary-${run.year}-${String(run.month).padStart(2, "0")}-${dateFrom}_${dateTo}.csv`,
    [header, ...body],
  );
}

export function exportInstructorPayoutLinesCsv(
  run: { year: number; month: number },
  dateFrom: string,
  dateTo: string,
  lines: (InstructorMonthlyPayoutLineDTO & { instructorName: string })[],
) {
  const header = [
    "\u0e84\u0eb9\u0eaa\u0ead\u0e99",
    "\u0ea7\u0eb1\u0e99\u0e97\u0eb5\u0ec8",
    "\u0ec0\u0ea7\u0ea5\u0eb2",
    "\u0eab\u0ec9\u0ead\u0e87",
    "\u0e8a\u0eb7\u0ec8/\u0e9c\u0eb9\u0ec9\u0e88\u0ead\u0e87",
    "\u0ec1\u0eab\u0ebc\u0ec8\u0e87",
    "source_id",
    "\u0eaa\u0eb0\u0e96\u0eb2\u0e99\u0e97\u0eb5\u0ec8",
    "\u0e9b\u0eb0\u0ec0\u0e9e\u0e94\u0e84\u0ec8\u0eb2\u0e88\u0ec9\u0eb2\u0e87",
    "\u0e96\u0eb2\u0e99",
    "\u0e8a\u0ebb\u0ec8\u0ea7\u0ec2\u0ea1\u0e87",
    "\u0e81\u0eb5\u0e9a/\u0eab\u0ebb\u0ea7\u0edc\u0ec8\u0ea7\u0e8d",
    "retainer_free",
    "month_session_index",
    "gross_kip",
    "\u0e84\u0ec8\u0eb2\u0e88\u0ec9\u0eb2\u0e87 (\u0e81\u0eb5\u0e9a)",
  ].join(",");

  const body = lines.map((line) =>
    [
      line.instructorName,
      line.date,
      line.startTime && line.endTime
        ? `${String(line.startTime).slice(0, 5)}-${String(line.endTime).slice(0, 5)}`
        : "",
      line.roomName ?? "",
      line.className ?? line.studentName ?? line.bookingLabel ?? "",
      sourceTypeLabel(line.sourceType),
      line.sourceId,
      sessionLocationLabel(
        line.sessionLocationKind,
        line.payoutLocationKind,
      ),
      payoutKindLabel(line.payoutWorkKind, line.payoutLocationKind),
      line.payoutBasis === "per_session" ? "\u0e95\u0ecd\u0ec8\u0e84\u0eb1\u0ec9\u0e87" : "\u0e95\u0ecd\u0ec8\u0e8a\u0ea1.",
      line.hours,
      line.amountKip,
      line.isRetainerFreeSession ? "1" : "0",
      line.monthGeneralClassLeadIndex ?? "",
      line.grossLinePayoutKip,
      line.linePayoutKip,
    ]
      .map(escapeCsvCell)
      .join(","),
  );

  downloadCsv(
    `instructor-monthly-payout-lines-${run.year}-${String(run.month).padStart(2, "0")}-${dateFrom}_${dateTo}.csv`,
    [header, ...body],
  );
}

export function computeBatchLiveDelta(
  batchInstructorIds: string[],
  batchSavedTotalKip: number,
  rows: InstructorSessionByInstructorItemDTO[],
): { savedTotal: number; liveTotal: number; delta: number } | null {
  const idSet = new Set(batchInstructorIds);
  let liveTotal = 0;
  for (const r of rows) {
    if (idSet.has(r.instructorId)) {
      liveTotal += r.estimatedPayout;
    }
  }
  const delta = liveTotal - batchSavedTotalKip;
  if (Math.abs(delta) === 0) return null;
  return {
    savedTotal: batchSavedTotalKip,
    liveTotal,
    delta,
  };
}
